import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { subscriptionAPI } from '../../lib/api';
import {
  Search,
  LayoutDashboard,
  PenTool,
  ShoppingCart,
  Users,
  Package,
  CheckSquare,
  CalendarDays,
  BarChart3,
  Wallet,
  Bell,
  Settings,
  HelpCircle,
  Globe,
  Crown,
  ChevronRight,
  ChevronDown,
  BookOpen,
  Lightbulb,
  ArrowUp,
  X,
  FileText,
  Shield,
  MessageSquare,
} from 'lucide-react';
import { cn } from '../../lib/utils';

/* ─────────────── Help Data ─────────────── */

const helpSections = [
  {
    id: 'getting-started',
    icon: BookOpen,
    title: 'Начало работы',
    color: 'text-violet-500',
    bg: 'bg-violet-500/10',
    articles: [
      {
        q: 'Что такое YaDela?',
        a: 'YaDela — это CRM-система для малого бизнеса и частных мастеров. Она помогает управлять заказами, клиентами, задачами и финансами в одном месте. Главная особенность — гибкий конструктор форм: вы сами настраиваете, какие данные собирать для вашего типа бизнеса.',
      },
      {
        q: 'Как зарегистрироваться?',
        a: 'Перейдите на страницу входа и нажмите «Регистрация». Укажите имя, email и пароль (минимум 8 символов). Также можно войти через Яндекс, Google или VK — аккаунт создастся автоматически. После регистрации вам придёт приветственное письмо.',
      },
      {
        q: 'Какие тарифы доступны?',
        a: '_TARIFF_OVERVIEW_',
      },
      {
        q: 'Как сбросить пароль?',
        a: 'На странице входа нажмите «Забыли пароль?», введите свой email. Вам придёт письмо со ссылкой для сброса пароля. Ссылка действует 1 час.',
      },
    ],
  },
  {
    id: 'dashboard',
    icon: LayoutDashboard,
    title: 'Дашборд',
    color: 'text-blue-500',
    bg: 'bg-blue-500/10',
    articles: [
      {
        q: 'Что показывает дашборд?',
        a: 'Дашборд — это главная страница с обзором вашего бизнеса. Здесь отображаются:\n\n• **Финансы** — прибыль, общий доход, расходы и сумма к получению\n• **Основные метрики** — количество заказов, клиентов, предоплат и задач\n• **Аналитика** — конверсия заказов, выполнение задач, процент сбора оплаты\n• **Графики** — доходы и расходы по месяцам, заказы по статусам, задачи по приоритетам, динамика клиентов\n• **Последние события** — недавние заказы и задачи',
      },
      {
        q: 'Как посмотреть статистику за определённый период?',
        a: 'Дашборд показывает общую статистику за всё время. Для просмотра данных за конкретный период перейдите в раздел «Отчёты» — там доступна фильтрация по датам и экспорт.',
      },
      {
        q: 'Как быстро создать заказ с дашборда?',
        a: 'В верхней части дашборда расположены кнопки быстрого создания: **+ Заказ**, **+ Задача** и **+ Клиент**. Нажмите нужную — откроется форма создания. Если заказов ещё нет, кнопка «Создать заказ» также появится в блоке последних заказов.',
      },
      {
        q: 'Учитывается ли часовой пояс?',
        a: 'Да. Система автоматически определяет ваш часовой пояс из настроек браузера. Все даты, графики и статистика отображаются с учётом вашего времени.',
      },
    ],
  },
  {
    id: 'form-builder',
    icon: PenTool,
    title: 'Конструктор форм',
    color: 'text-pink-500',
    bg: 'bg-pink-500/10',
    articles: [
      {
        q: 'Зачем нужен конструктор форм?',
        a: 'Конструктор форм — ключевая функция YaDela. Он позволяет создать уникальную форму заказа для вашего бизнеса. Например, кондитер может добавить поля «Вкус торта», «Количество ярусов», «Дата доставки», а организатор мероприятий — «Дата события», «Количество гостей», «Тип площадки».',
      },
      {
        q: 'Какие типы полей доступны?',
        a: 'Доступно 12 типов полей:\n\n• **Текст (строка)** — короткий текстовый ввод\n• **Число** — числовой ввод\n• **Текст (абзац)** — большое многострочное текстовое поле\n• **Выбор из списка** — выпадающий список для выбора одного варианта\n• **Выбор (видны все)** — радио-кнопки, все варианты видны сразу\n• **Выбор нескольких** — чекбоксы для выбора нескольких вариантов\n• **Да/Нет (галочка)** — одна галочка для подтверждения\n• **Дата** — выбор даты через календарь\n• **Время** — выбор времени (с поддержкой уведомлений!)\n• **Загрузка файла** — загрузка документов и изображений\n• **Галерея фото** — загрузка нескольких изображений с предпросмотром\n• **Разделитель (заголовок)** — визуальный разделитель для группировки полей',
      },
      {
        q: 'Как использовать шаблоны?',
        a: 'При создании формы нажмите кнопку «Из шаблона». Доступны готовые шаблоны:\n\n• 🎮 Бронь игровой комнаты (7 полей)\n• 🎂 Заказ торта (11 полей)\n• 💅 Запись на маникюр\n• 📸 Заказ фотосессии\n• 🐾 Запись на груминг\n\nШаблон автоматически заполнит все поля — вы можете их изменить под себя.',
      },
      {
        q: 'Что такое условная видимость?',
        a: 'Вы можете настроить поле так, чтобы оно показывалось только при определённом значении другого поля. Например: поле «Текст на торте» появляется только если в поле «Нужна надпись» выбрано «Да».',
      },
      {
        q: 'Как работает Drag-and-Drop?',
        a: 'Поля формы можно перетаскивать мышкой (или пальцем на мобильном) для изменения порядка. Зажмите поле и перетащите его на нужную позицию.',
      },
      {
        q: 'Как сделать поле обязательным?',
        a: 'В настройках поля включите переключатель «Обязательное». Такое поле будет отмечено красной звёздочкой (*), и заказ нельзя будет создать без его заполнения.',
      },
    ],
  },
  {
    id: 'orders',
    icon: ShoppingCart,
    title: 'Заказы',
    color: 'text-emerald-500',
    bg: 'bg-emerald-500/10',
    articles: [
      {
        q: 'Как создать заказ?',
        a: 'Нажмите «Новый заказ» — откроется пошаговый визард из 4 шагов:\n\n1. **Форма** — выберите форму заказа (которую вы создали в конструкторе)\n2. **Клиент** — выберите существующего клиента или создайте нового\n3. **Данные** — заполните все поля формы\n4. **Оплата** — укажите статус, сумму и предоплату\n\nСистема автоматически рассчитает остаток к оплате.',
      },
      {
        q: 'Какие статусы у заказов?',
        a: '• **Новый** — только создан\n• **В работе** — взят в работу\n• **Завершён** — выполнен\n• **Отменён** — отменён клиентом или вами',
      },
      {
        q: 'Как работает нумерация?',
        a: 'Каждый заказ получает автоматический порядковый номер (№1, №2, №3...). Нумерация ведётся отдельно для каждого пользователя.',
      },
      {
        q: 'Как редактировать заказ?',
        a: 'На странице заказов нажмите иконку карандаша (✏️) на карточке заказа. Откроется боковая панель редактирования, где можно изменить статус, суммы и данные формы.',
      },
      {
        q: 'Как экспортировать заказы?',
        a: 'Перейдите в раздел «Отчёты» и нажмите «Экспорт заказов в CSV». Будет загружен файл со всеми заказами, включая данные полей формы.',
      },
      {
        q: 'Что такое уведомления о времени?',
        a: 'Если в форме есть поле типа «Время» и вы включили уведомление, система напомнит вам за заданное время (например, за 30 минут) до указанного часа. Удобно для напоминания о доставке или встрече.',
      },
    ],
  },
  {
    id: 'customers',
    icon: Users,
    title: 'Клиенты',
    color: 'text-orange-500',
    bg: 'bg-orange-500/10',
    articles: [
      {
        q: 'Какую информацию можно хранить о клиенте?',
        a: 'Для каждого клиента доступны:\n\n• Имя, телефон, email, адрес\n• Заметки\n• Социальные сети: Telegram, WhatsApp, Instagram, VK, Одноклассники, Max, Facebook\n• Автоматическая статистика: количество заказов и общая сумма покупок',
      },
      {
        q: 'Как привязать клиента к заказу?',
        a: 'При создании заказа на шаге 2 (Клиент) выберите клиента из списка или создайте нового прямо в процессе. Клиент будет привязан к заказу, а его статистика обновится автоматически.',
      },
      {
        q: 'Откуда берётся статистика клиента?',
        a: 'Количество заказов и сумма покупок рассчитываются в реальном времени на основе всех заказов, привязанных к клиенту. Если вы измените сумму заказа — статистика обновится автоматически.',
      },
      {
        q: 'Можно ли экспортировать базу клиентов?',
        a: 'Да. Перейдите в раздел «Отчёты» и нажмите «Экспорт клиентов в CSV». Будет загружен файл с именами, контактами, соцсетями и статистикой всех ваших клиентов.',
      },
    ],
  },
  {
    id: 'tasks',
    icon: CheckSquare,
    title: 'Задачи',
    color: 'text-cyan-500',
    bg: 'bg-cyan-500/10',
    articles: [
      {
        q: 'Как устроена доска задач?',
        a: 'Задачи отображаются на Kanban-доске с тремя колонками:\n\n• **Ожидает** — новые задачи\n• **В работе** — текущие задачи\n• **Выполнено** — завершённые задачи\n\nКарточки задач показывают приоритет, дедлайн и привязанный заказ.',
      },
      {
        q: 'Какие приоритеты есть?',
        a: '• 🟢 **Низкий** — не срочно\n• 🟡 **Средний** — обычная задача\n• 🔴 **Высокий** — срочная задача\n\nВысокий приоритет выделяется красным цветом на карточке.',
      },
      {
        q: 'Можно ли привязать задачу к заказу?',
        a: 'Да. При создании задачи вы можете привязать её к заказу или счёту. На карточке задачи будет отображаться номер заказа или счёта для быстрой навигации.',
      },
      {
        q: 'Как настроить напоминание?',
        a: 'Включите уведомление при создании задачи и установите срок выполнения. Система отправит напоминание в Telegram за указанное время до дедлайна (настраивается в «Настройки» → «Уведомления»).',
      },
      {
        q: 'Как экспортировать задачи в календарь?',
        a: 'Есть два способа:\n\n• **Подписка** — на странице «Календарь» внизу есть персональная ссылка .ics. Добавьте её в Google Calendar, Apple Calendar или Outlook для автоматической синхронизации.\n• **Скачивание** — в разделе «Отчёты» нажмите «Экспорт задач в ICS». Файл можно импортировать в любое приложение-календарь.',
      },
    ],
  },
  {
    id: 'materials',
    icon: Package,
    title: 'Материалы',
    color: 'text-amber-600',
    bg: 'bg-amber-600/10',
    articles: [
      {
        q: 'Для чего раздел «Материалы»?',
        a: 'Раздел помогает вести учёт расходных материалов и инвентаря. Вы можете отслеживать остатки, устанавливать минимальные запасы и видеть, когда пора закупаться.',
      },
      {
        q: 'Какие поля есть у материала?',
        a: '• Название и описание\n• Количество и единица измерения\n• Минимальный остаток (порог для предупреждения)\n• Цена за единицу\n• Категория',
      },
      {
        q: 'Как узнать, что материал заканчивается?',
        a: 'Если текущее количество материала ниже минимального остатка, карточка выделяется предупреждающим цветом. Это позволяет вовремя пополнить запасы.',
      },
    ],
  },
  {
    id: 'calendar',
    icon: CalendarDays,
    title: 'Календарь',
    color: 'text-indigo-500',
    bg: 'bg-indigo-500/10',
    articles: [
      {
        q: 'Что показывает календарь?',
        a: 'Календарь отображает задачи и заказы на временной шкале. Доступны два режима просмотра: **Месяц** (сетка с мини-карточками) и **Неделя** (подробный вид с расширенными карточками). Можно фильтровать события по типу (заказы/задачи) и статусу. Нажатие на день открывает подробный список событий.',
      },
      {
        q: 'Можно ли подписаться на календарь?',
        a: 'Да! На странице «Календарь» внизу есть персональная ссылка формата .ics. Добавьте её в Google Calendar, Apple Calendar или Outlook — и ваши задачи и заказы будут синхронизироваться автоматически. На iPhone нажмите ссылку — телефон предложит «Подписаться на календарь».',
      },
    ],
  },
  {
    id: 'finance',
    icon: Wallet,
    title: 'Финансы',
    color: 'text-green-600',
    bg: 'bg-green-600/10',
    articles: [
      {
        q: 'Как вести учёт финансов?',
        a: 'Раздел «Финансы» позволяет вести учёт доходов и расходов:\n\n1. Создайте категории (например, «Материалы», «Доставка», «Оплата от клиента»)\n2. Добавляйте транзакции — доход или расход с суммой, датой и описанием\n3. Смотрите сводку: общий доход, расходы и прибыль',
      },
      {
        q: 'Есть ли графики?',
        a: 'Да, в разделе доступна визуализация финансов по периодам. Вы можете видеть динамику доходов и расходов на графике и фильтровать по категориям и датам.',
      },
      {
        q: 'Как фильтровать транзакции?',
        a: 'Используйте фильтры вверху списка:\n\n• **По периоду** — Всё время, Месяц (с выбором конкретного месяца), Год\n• **По типу** — доходы или расходы\n• **По категории** — выберите одну из ваших категорий\n\nВ верхней части страницы также доступны сводные карточки (доход, расходы, прибыль), графики по месяцам и диаграммы расходов/доходов по категориям — все они обновляются в соответствии с выбранным периодом.',
      },
      {
        q: 'Связаны ли финансы с заказами?',
        a: 'Да. При завершении заказа система автоматически создаёт транзакцию дохода на сумму заказа. Если вы измените сумму завершённого заказа — транзакция обновится. При отмене завершения — транзакция удалится. Также вы можете создавать ручные транзакции с категориями для детального учёта.',
      },
    ],
  },
  {
    id: 'invoices',
    icon: FileText,
    title: 'Счета',
    color: 'text-sky-600',
    bg: 'bg-sky-600/10',
    articles: [
      {
        q: 'Для чего раздел «Счета»?',
        a: 'Раздел позволяет выставлять счета клиентам с публичной ссылкой для оплаты. Каждый счёт получает уникальную публичную страницу, которую можно отправить клиенту. Клиент увидит сумму, описание и реквизиты для оплаты.',
      },
      {
        q: 'Как создать счёт?',
        a: 'Нажмите «Новый счёт» на странице счетов. Укажите сумму, описание и при необходимости привяжите клиента. После создания счёт получит публичную ссылку, которую можно скопировать и отправить клиенту.',
      },
      {
        q: 'Какие статусы у счетов?',
        a: '• **Новый** — только создан, ожидает оплаты\n• **Оплачен** — клиент оплатил\n• **Отменён** — счёт отменён',
      },
      {
        q: 'Как поделиться счётом с клиентом?',
        a: 'На карточке счёта нажмите иконку ссылки — публичный URL скопируется в буфер обмена. Отправьте ссылку клиенту в мессенджере или по email. Клиент увидит красиво оформленную страницу с деталями счёта и реквизитами.',
      },
    ],
  },
  {
    id: 'reports',
    icon: BarChart3,
    title: 'Отчёты и экспорт',
    color: 'text-purple-500',
    bg: 'bg-purple-500/10',
    articles: [
      {
        q: 'Какие отчёты доступны?',
        a: 'В разделе «Отчёты» доступны графики и экспорт:\n\n• **Графики доходов и расходов** по месяцам\n• **Графики клиентов** — динамика привлечения\n• **Распределение заказов** по статусам (круговая диаграмма)\n• **Метрики** — средний чек, конверсия, общий доход\n\nТакже можно экспортировать данные:\n• **Заказы → CSV** — все заказы с данными полей формы\n• **Задачи → CSV** — все задачи с приоритетами и дедлайнами\n• **Клиенты → CSV** — база клиентов с контактами\n• **Задачи → ICS** — задачи в формате календаря',
      },
      {
        q: 'Как скачать отчёт?',
        a: 'Перейдите в раздел «Отчёты» и нажмите соответствующую кнопку экспорта. Файл автоматически загрузится на ваше устройство.',
      },
    ],
  },
  {
    id: 'notifications',
    icon: Bell,
    title: 'Уведомления',
    color: 'text-rose-500',
    bg: 'bg-rose-500/10',
    articles: [
      {
        q: 'Как привязать Telegram?',
        a: 'Перейдите в «Настройки» → «Уведомления» → нажмите «Привязать Telegram». Система сгенерирует уникальный код. Нажмите на ссылку бота и отправьте ему команду /start с этим кодом. Привязка произойдёт автоматически.',
      },
      {
        q: 'Какие уведомления приходят?',
        a: 'После привязки Telegram и включения уведомлений вы получите:\n\n• 🔐 **Вход в систему** — кто и когда вошёл\n• 📦 **Новый заказ** — номер, клиент, сумма, статус\n• ✅ **Новая задача** — название, приоритет, дедлайн\n• ⏰ **Напоминания** — за N минут до дедлайна задачи\n• 📅 **Напоминания о заказах** — для полей с временем\n\nТакже доступны **Push-уведомления в браузере** — включите их в «Настройки» → «Уведомления» для получения мгновенных уведомлений прямо на рабочем столе.',
      },
      {
        q: 'Как настроить время напоминания?',
        a: 'В «Настройки» → «Уведомления» есть поле «Напоминать за». Доступные варианты: 5, 10, 15, 30 минут, 1 час, 2 часа, 1 день. Напоминание придёт в Telegram за это время до дедлайна.',
      },
      {
        q: 'Приходят ли уведомления на email?',
        a: 'Да. Система отправляет красиво оформленные HTML-письма при входе, создании заказов и задач. Также email используется для сброса пароля и сообщений поддержки.',
      },
    ],
  },
  {
    id: 'my-page',
    icon: Globe,
    title: 'Моя страница',
    color: 'text-teal-500',
    bg: 'bg-teal-500/10',
    articles: [
      {
        q: 'Что такое «Моя страница»?',
        a: 'Это ваша публичная страница-визитка, доступная по ссылке вида yadela.ru/ваш-адрес. Вы можете поделиться ей с клиентами.',
      },
      {
        q: 'Что можно разместить на странице?',
        a: 'На публичной странице доступны:\n\n• Имя и описание деятельности\n• Фотография профиля\n• Контактная информация\n• Ссылки на социальные сети\n• Список услуг с ценами\n• Портфолио фотографий ваших работ',
      },
      {
        q: 'Как настроить адрес страницы?',
        a: 'В редакторе публичной страницы укажите желаемый адрес. Система проверит его уникальность и доступность. Используйте латинские буквы, цифры и дефисы (от 3 до 50 символов). Системные адреса (login, orders, admin и др.) зарезервированы.',
      },
    ],
  },
  {
    id: 'settings',
    icon: Settings,
    title: 'Настройки',
    color: 'text-slate-500',
    bg: 'bg-slate-500/10',
    articles: [
      {
        q: 'Как изменить профиль?',
        a: 'В разделе «Настройки» вы можете изменить имя, телефон и другие данные профиля. Если вы вошли через OAuth (Яндекс, Google, VK), вы также можете установить пароль для входа по email.',
      },
      {
        q: 'Как сменить тему?',
        a: 'Тема переключается кнопкой в нижней части бокового меню: Светлая → Тёмная → Авто. Режим «Авто» подхватывает системную тему вашего устройства. Также можно выбрать тему в «Настройки».',
      },
      {
        q: 'Я вошёл через соцсеть. Как установить пароль?',
        a: 'Перейдите в «Настройки» и найдите блок «Установить пароль». Задайте пароль (минимум 8 символов). После этого вы сможете входить как через соцсеть, так и по email + паролю.',
      },
      {
        q: 'Как настроить уведомления?',
        a: 'В «Настройки» → «Уведомления» вы можете:\n\n• Включить/выключить Telegram, Email и Push (браузерные) уведомления\n• Выбрать, о чём уведомлять (вход, новый заказ, новая задача)\n• Настроить время напоминания (от 5 минут до 1 дня)\n• Подписаться на Push-уведомления в браузере — они работают даже когда вкладка с YaDela закрыта',
      },
      {
        q: 'Как удалить аккаунт?',
        a: 'В «Настройки» внизу есть кнопка «Удалить аккаунт». Для подтверждения потребуется ввести слово «Delete». При удалении все ваши данные (заказы, клиенты, задачи, формы) будут удалены безвозвратно.',
      },
    ],
  },
  {
    id: 'support',
    icon: MessageSquare,
    title: 'Поддержка',
    color: 'text-sky-500',
    bg: 'bg-sky-500/10',
    articles: [
      {
        q: 'Как связаться с поддержкой?',
        a: 'Перейдите в раздел «Поддержка» в боковом меню. Нажмите «Новое обращение», заполните тему и описание вопроса. Сообщение будет отправлено команде поддержки, а копия — на ваш email. Также доступен резервный канал связи через Telegram — ссылка указана в верхней части страницы.',
      },
      {
        q: 'Можно ли отслеживать статус обращения?',
        a: 'Да. Все ваши обращения сохраняются в разделе «Поддержка» с историей переписки. Вы увидите статус: **Новый**, **В работе**, **Решён** или **Закрыт**. Когда поддержка ответит — вы сможете продолжить диалог. Фильтр по статусу поможет быстро найти нужное обращение.',
      },
      {
        q: 'Что происходит с закрытыми обращениями?',
        a: 'Закрытые и решённые обращения автоматически удаляются через 3 дня вместе со всеми вложениями. Если вопрос возникнет снова — просто создайте новое обращение.',
      },
    ],
  },
  {
    id: 'subscription',
    icon: Crown,
    title: 'Подписка и тарифы',
    color: 'text-yellow-500',
    bg: 'bg-yellow-500/10',
    dynamic: true, // Marker: articles filled from API
    articles: [
      {
        q: 'Чем отличается Демо от Про?',
        a: '_DEMO_VS_PRO_',
      },
      {
        q: 'Сколько стоит Про?',
        a: '_PRICING_TABLE_',
      },
      {
        q: 'Что будет, если лимит исчерпан?',
        a: 'При попытке создать заказ, задачу, клиента или материал сверх лимита демо-тарифа появится диалог с предложением перейти на Про. Существующие данные не пропадут — вы просто не сможете добавлять новые, пока не оформите подписку.',
      },
      {
        q: 'Как оформить подписку?',
        a: 'Нажмите на плашку вашего тарифа внизу бокового меню (например, «Демо — Улучшить»). Откроется страница тарифов, где можно выбрать подходящий период и оплатить. Подписка активируется мгновенно.',
      },
      {
        q: 'Можно ли отменить подписку?',
        a: 'Подписка действует до окончания оплаченного периода. После истечения срока аккаунт переходит на Демо-тариф. Данные сохраняются, но создание новых записей сверх лимита будет ограничено.',
      },
    ],
  },
  {
    id: 'security',
    icon: Shield,
    title: 'Безопасность',
    color: 'text-red-500',
    bg: 'bg-red-500/10',
    articles: [
      {
        q: 'Как хранятся пароли?',
        a: 'Все пароли хешируются с помощью bcrypt — даже администраторы не имеют доступа к вашему паролю в открытом виде.',
      },
      {
        q: 'Какие способы входа доступны?',
        a: '• Email + пароль\n• Яндекс ID\n• Google аккаунт\n• VK ID\n\nПри входе через соцсеть вы можете дополнительно установить пароль для входа по email.',
      },
      {
        q: 'Мои данные в безопасности?',
        a: 'Да. Передача данных защищена HTTPS-шифрованием. Мы не передаём и не продаём ваши данные третьим лицам. Каждый пользователь видит только свои данные — заказы, клиенты и задачи изолированы.',
      },
    ],
  },
];

/* ─────────────── Markdown-lite renderer ─────────────── */

function RichText({ text }) {
  const lines = text.split('\n');
  return (
    <div className="space-y-1.5">
      {lines.map((line, i) => {
        if (line.trim() === '') return <div key={i} className="h-1.5" />;

        const isBullet = line.trimStart().startsWith('•');
        const rawLine = isBullet ? line.trimStart().slice(1).trimStart() : line;

        // bold **text**
        const parts = rawLine.split(/(\*\*[^*]+\*\*)/g).map((part, j) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={j} className="font-semibold text-foreground">{part.slice(2, -2)}</strong>;
          }
          return <span key={j}>{part}</span>;
        });

        if (isBullet) {
          return (
            <div key={i} className="flex gap-1.5 pl-1">
              <span className="text-muted-foreground shrink-0">•</span>
              <span>{parts}</span>
            </div>
          );
        }
        return <p key={i}>{parts}</p>;
      })}
    </div>
  );
}

/* ─────────────── Component ─────────────── */

export default function HelpPage() {
  const [search, setSearch] = useState('');
  const [openArticles, setOpenArticles] = useState({});
  const [activeSection, setActiveSection] = useState(null);
  const sectionRefs = useRef({});
  const contentRef = useRef(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [plans, setPlans] = useState(null);

  // Load pricing from API
  useEffect(() => {
    subscriptionAPI.getPlans()
      .then((res) => setPlans(res.data))
      .catch(() => setPlans(null));
  }, []);

  // Build pricing text dynamically
  const buildPricingText = useCallback(() => {
    if (!plans?.pro?.periods) return 'Информация о ценах загружается...';
    const lines = ['Стоимость зависит от периода:\n'];
    const periodLabels = { 1: '1 месяц', 3: '3 месяца', 6: '6 месяцев', 12: '12 месяцев' };
    plans.pro.periods.forEach((p) => {
      const label = periodLabels[p.period_months] || `${p.period_months} мес`;
      const total = new Intl.NumberFormat('ru-RU').format(p.total);
      const perMonth = new Intl.NumberFormat('ru-RU').format(p.price_per_month);
      if (p.discount_percent > 0) {
        lines.push(`• **${label}** — ${total} ₽ (${perMonth} ₽/мес, скидка ${p.discount_percent}%)`);
      } else {
        lines.push(`• **${label}** — ${total} ₽`);
      }
    });
    return lines.join('\n');
  }, [plans]);

  const buildDemoVsProText = useCallback(() => {
    const demoLimits = plans?.demo?.limits;
    const ordersL = demoLimits?.orders ?? 3;
    const tasksL = demoLimits?.tasks ?? 3;
    const customersL = demoLimits?.customers ?? 3;
    const materialsL = demoLimits?.materials ?? 1;
    const invoicesL = demoLimits?.invoices ?? 3;
    return `**Демо (бесплатно):**\n• До ${ordersL} заказов, ${tasksL} задач, ${customersL} клиентов, ${invoicesL} счетов и ${materialsL} материалов\n• Все основные функции: конструктор форм, финансы, уведомления, календарь, отчёты, экспорт\n• Подходит для знакомства с системой\n\n**Про (платно):**\n• Безлимитные заказы, задачи, клиенты, счета и материалы\n• Публичная страница бизнеса с портфолио\n• Приоритетная поддержка`;
  }, [plans]);

  const buildTariffOverview = useCallback(() => {
    const demoLimits = plans?.demo?.limits;
    const ordersL = demoLimits?.orders ?? 3;
    const tasksL = demoLimits?.tasks ?? 3;
    const customersL = demoLimits?.customers ?? 3;
    const materialsL = demoLimits?.materials ?? 1;
    const invoicesL = demoLimits?.invoices ?? 3;
    const minPrice = plans?.pro?.periods?.length
      ? Math.min(...plans.pro.periods.map(p => p.price_per_month))
      : null;
    const minPriceStr = minPrice ? `от ${new Intl.NumberFormat('ru-RU').format(minPrice)} ₽/мес` : '';
    return `Есть два тарифа:\n\n• **Демо** (бесплатно) — до ${ordersL} заказов, ${tasksL} задач, ${customersL} клиентов, ${invoicesL} счетов и ${materialsL} материалов. Все основные функции доступны. Подходит для знакомства.\n• **Про** — безлимитные заказы, задачи, клиенты, счета и материалы + публичная страница бизнеса. ${minPriceStr ? `Стоимость ${minPriceStr} при оплате за год.` : ''}`;
  }, [plans]);

  // Resolve dynamic placeholders in article text
  const resolveText = useCallback((text) => {
    if (text === '_PRICING_TABLE_') return buildPricingText();
    if (text === '_DEMO_VS_PRO_') return buildDemoVsProText();
    if (text === '_TARIFF_OVERVIEW_') return buildTariffOverview();
    return text;
  }, [buildPricingText, buildDemoVsProText, buildTariffOverview]);

  // Sections with resolved dynamic text
  const sections = useMemo(() => {
    return helpSections.map((s) => ({
      ...s,
      articles: s.articles.map((a) => ({ ...a, a: resolveText(a.a) })),
    }));
  }, [resolveText]);

  // Filter sections by search
  const filtered = useMemo(() => {
    if (!search.trim()) return sections;
    const q = search.toLowerCase();
    return sections
      .map((section) => ({
        ...section,
        articles: section.articles.filter(
          (a) => a.q.toLowerCase().includes(q) || a.a.toLowerCase().includes(q)
        ),
      }))
      .filter((s) => s.articles.length > 0);
  }, [search, sections]);

  // Auto-expand found articles when searching
  useEffect(() => {
    if (search.trim()) {
      const newOpen = {};
      filtered.forEach((s) =>
        s.articles.forEach((a) => {
          newOpen[`${s.id}-${a.q}`] = true;
        })
      );
      setOpenArticles(newOpen);
    } else {
      setOpenArticles({});
    }
  }, [search, filtered]);

  // Scroll spy
  useEffect(() => {
    const container = contentRef.current;
    if (!container) return;
    const handler = () => {
      setShowScrollTop(container.scrollTop > 400);
      const offsets = Object.entries(sectionRefs.current).map(([id, el]) => ({
        id,
        top: el ? el.getBoundingClientRect().top : Infinity,
      }));
      const visible = offsets.filter((o) => o.top < 300).sort((a, b) => b.top - a.top);
      if (visible.length > 0) setActiveSection(visible[0].id);
    };
    container.addEventListener('scroll', handler, { passive: true });
    return () => container.removeEventListener('scroll', handler);
  }, []);

  const toggleArticle = (key) => {
    setOpenArticles((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const scrollToSection = (id) => {
    const el = sectionRefs.current[id];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(id);
    }
  };

  const scrollToTop = () => {
    contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const totalArticles = sections.reduce((acc, s) => acc + s.articles.length, 0);

  return (
    <div ref={contentRef}>
      <div className="pb-24">
        {/* Page header */}
        <div className="mb-6">
          <div>
            <h1 className="text-lg font-semibold tracking-tight text-foreground">Справка</h1>
            <p className="text-sm text-muted-foreground">Ответы на вопросы по всем функциям YaDela</p>
          </div>

          {/* Search */}
          <div className="relative max-w-xl">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Поиск по справке..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 pr-10 h-10"
              data-testid="help-search"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 rounded-full bg-muted flex items-center justify-center hover:bg-muted-foreground/20 transition-colors"
              >
                <X className="h-3 w-3 text-muted-foreground" />
              </button>
            )}
          </div>

          {search.trim() && (
            <p className="text-xs text-muted-foreground mt-2">
              Найдено: {filtered.reduce((a, s) => a + s.articles.length, 0)} статей в {filtered.length} разделах
            </p>
          )}
        </div>

        {/* Quick nav chips (desktop) */}
        {!search.trim() && (
          <div className="hidden sm:flex flex-wrap gap-2 mb-8">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors',
                  activeSection === section.id
                    ? 'bg-primary/10 border-primary/30 text-primary'
                    : 'bg-card border-border/60 text-muted-foreground shadow-sm hover:text-foreground hover:border-foreground/20'
                )}
              >
                <section.icon className={cn('h-3.5 w-3.5', activeSection === section.id ? 'text-primary' : section.color)} />
                {section.title}
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        {filtered.length === 0 ? (
          <div className="text-center py-16">
            <Search className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-1">Ничего не найдено</h3>
            <p className="text-sm text-muted-foreground">Попробуйте другой запрос или очистите поиск</p>
          </div>
        ) : (
          filtered.map((section, idx) => (
            <div
              key={section.id}
              ref={(el) => { sectionRefs.current[section.id] = el; }}
              className={cn('scroll-mt-4', idx > 0 && 'mt-10 pt-8 border-t border-border')}
            >
              {/* Section header */}
              <div className="flex items-center gap-3 mb-4">
                <div className={cn('h-9 w-9 rounded-lg flex items-center justify-center shrink-0', section.bg)}>
                  <section.icon className={cn('h-[18px] w-[18px]', section.color)} />
                </div>
                <h2 className="text-lg font-bold text-foreground">{section.title}</h2>
                <Badge variant="outline" className="text-[10px] h-5 px-1.5 ml-1 shrink-0">
                  {section.articles.length}
                </Badge>
              </div>

              {/* Articles */}
              <div className="flex flex-col gap-2.5">
                {section.articles.map((article) => {
                  const key = `${section.id}-${article.q}`;
                  const isOpen = !!openArticles[key];
                  return (
                    <div
                      key={key}
                      className={cn(
                        'rounded-xl border border-border transition-all',
                        isOpen ? 'bg-card shadow-md ring-1 ring-primary/10' : 'bg-card shadow-sm hover:shadow-md'
                      )}
                    >
                      <button
                        onClick={() => toggleArticle(key)}
                        className="flex items-start gap-3 w-full text-left px-4 py-3.5"
                        data-testid={`help-article-${section.id}`}
                      >
                        <div className="shrink-0 mt-0.5">
                          {isOpen ? (
                            <ChevronDown className="h-4 w-4 text-primary" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          )}
                        </div>
                        <span className={cn(
                          'text-sm font-medium leading-snug',
                          isOpen ? 'text-primary' : 'text-foreground'
                        )}>
                          {article.q}
                        </span>
                      </button>
                      {isOpen && (
                        <div className="px-4 pb-4 pl-11">
                          <div className="text-sm text-muted-foreground leading-relaxed">
                            <RichText text={article.a} />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}

        {/* Tip at the bottom */}
        {!search.trim() && (
          <div className="flex items-start gap-3 rounded-xl border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-950/20 p-4 mt-10">
            <Lightbulb className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Не нашли ответ?</p>
              <p className="text-sm text-amber-700/80 dark:text-amber-400/70 mt-0.5">
                Напишите нам в разделе «Поддержка» — мы ответим в кратчайшие сроки.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Scroll to top */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 z-30 h-10 w-10 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:bg-primary/90 transition-all"
          title="Наверх"
        >
          <ArrowUp className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
